import "@/components/workspace/workspace.css";
import { ShortcutPanel } from "@/components/accounting/shortcut-panel";
import {
	type MenuBarItem,
	type StatusBarItem,
	Workspace,
	type WorkspaceConfig,
	useWorkspaceContext,
} from "@/components/workspace";
import {
	useGlobalAccountingShortcuts,
	useShortcutPanel,
} from "@/hooks/use-accounting-shortcuts";
import {
	useAccountingContext,
	useAutoSelectPeriod,
} from "@/lib/accounting-context";
import { useQueryClient } from "@tanstack/react-query";
import { ModuleErrorBoundary } from "@/components/ModuleErrorBoundary";
import {
	Outlet,
	createFileRoute,
	useNavigate,
} from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface AccountingSearch {
	period?: string;
	basis?: string;
}

export const Route = createFileRoute("/accounting")({
	component: AccountingLayout,
	validateSearch: (search: Record<string, unknown>): AccountingSearch => ({
		period: typeof search.period === "string" ? search.period : undefined,
		basis: typeof search.basis === "string" ? search.basis : undefined,
	}),
});

// ── Menu bar definition ─────────────────────────────────────────────

function useMenuBar(): MenuBarItem[] {
	const { t } = useTranslation("accounting");
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	return useMemo<MenuBarItem[]>(
		() => [
			{
				id: "file",
				label: t("menu_file", { defaultValue: "Файл" }),
				items: [
					{
						id: "new",
						label: t("menu_new_entry", {
							defaultValue: "Создать проводку",
						}),
						shortcut: "Ctrl+N",
						onClick: () => navigate({ to: "/accounting/journal/new" }),
					},
					{
						id: "save",
						label: t("menu_save", { defaultValue: "Сохранить" }),
						shortcut: "Ctrl+S",
						disabled: true,
					},
					{ id: "sep1", label: "", separator: true },
					{
						id: "print",
						label: t("menu_print", { defaultValue: "Печать..." }),
						shortcut: "Ctrl+P",
						onClick: () => window.print(),
					},
					{
						id: "export",
						label: t("menu_export", { defaultValue: "Экспорт" }),
						children: [
							{
								id: "excel",
								label: "Excel",
							},
							{
								id: "csv",
								label: "CSV",
							},
						],
					},
				],
			},
			{
				id: "edit",
				label: t("menu_edit", { defaultValue: "Правка" }),
				items: [
					{
						id: "undo",
						label: t("menu_undo", { defaultValue: "Отменить" }),
						shortcut: "Ctrl+Z",
						disabled: true,
					},
					{
						id: "redo",
						label: t("menu_redo", { defaultValue: "Повторить" }),
						shortcut: "Ctrl+Y",
						disabled: true,
					},
				],
			},
			{
				id: "view",
				label: t("menu_view", { defaultValue: "Вид" }),
				items: [
					{
						id: "refresh",
						label: t("menu_refresh", { defaultValue: "Обновить" }),
						shortcut: "F5",
						onClick: () => queryClient.invalidateQueries(),
					},
				],
			},
			{
				id: "operations",
				label: t("menu_operations", { defaultValue: "Операции" }),
				items: [
					{
						id: "post",
						label: t("menu_post", { defaultValue: "Провести" }),
						shortcut: "Ctrl+Enter",
						disabled: true,
					},
					{
						id: "reverse",
						label: t("menu_reverse", { defaultValue: "Сторнировать" }),
						disabled: true,
					},
					{
						id: "recurring_entries",
						label: t("menu_recurring_entries", {
							defaultValue: "Повторяющиеся проводки",
						}),
						onClick: () => navigate({ to: "/accounting/recurring" }),
					},
					{
						id: "regulatory_operations",
						label: t("menu_regulatory_operations", {
							defaultValue: "Регламентные операции",
						}),
						onClick: () => navigate({ to: "/accounting/operations" }),
					},
					{
						id: "close_period",
						label: t("menu_close_period", {
							defaultValue: "Закрыть период",
						}),
						onClick: () => navigate({ to: "/accounting/close" }),
					},
				],
			},
			{
				id: "reports",
				label: t("menu_reports", { defaultValue: "Отчёты" }),
				items: [
					{
						id: "osv",
						label: t("menu_osv", {
							defaultValue: "ОСВ (Оборотно-сальдовая ведомость)",
						}),
						onClick: () => navigate({ to: "/accounting/statements" }),
					},
					{
						id: "pnl",
						label: t("menu_pnl", {
							defaultValue: "Отчёт о прибылях и убытках",
						}),
						onClick: () => navigate({ to: "/accounting/statements" }),
					},
					{
						id: "balance",
						label: t("menu_balance_sheet", {
							defaultValue: "Бухгалтерский баланс",
						}),
						onClick: () => navigate({ to: "/accounting/statements" }),
					},
					{
						id: "cashflow",
						label: t("menu_cashflow", {
							defaultValue: "Отчёт о движении денежных средств",
						}),
						onClick: () => navigate({ to: "/accounting/statements" }),
					},
					{
						id: "account_card",
						label: t("menu_account_card", {
							defaultValue: "Карточка счёта",
						}),
						onClick: () => navigate({ to: "/accounting/accounts" }),
					},
					{
						id: "account_analysis",
						label: t("menu_account_analysis", {
							defaultValue: "Анализ счёта",
						}),
						onClick: () => navigate({ to: "/accounting/analysis" }),
					},
					{
						id: "chess_balance",
						label: t("menu_chess_balance", {
							defaultValue: "Шахматная ведомость",
						}),
						onClick: () => navigate({ to: "/accounting/crosstab" }),
					},
					{
						id: "counterparty_recon",
						label: t("menu_counterparty_recon", {
							defaultValue: "Акт сверки",
						}),
						onClick: () =>
							navigate({ to: "/accounting/counterparty-recon" }),
					},
				],
			},
			{
				id: "tools",
				label: t("menu_tools", { defaultValue: "Сервис" }),
				items: [
					{
						id: "settings",
						label: t("menu_settings", { defaultValue: "Настройки" }),
						disabled: true,
					},
					{
						id: "autonomy",
						label: t("menu_autonomy", {
							defaultValue: "Уровни автономии AI",
						}),
						disabled: true,
					},
					{
						id: "quality_check",
						label: t("menu_quality_check", {
							defaultValue: "Экспресс-проверка",
						}),
						onClick: () =>
							navigate({ to: "/accounting/quality-check" }),
					},
					{
						id: "schemas",
						label: t("menu_schemas", {
							defaultValue: "Схемы проводок",
						}),
						onClick: () => navigate({ to: "/accounting/schemas" }),
					},
				],
			},
		],
		[t, navigate, queryClient],
	);
}

// ── Inner layout (uses workspace context) ───────────────────────────

function AccountingWorkspaceInner() {
	const { t } = useTranslation("accounting");
	const menuBar = useMenuBar();
	const { statusBar } = useWorkspaceContext();
	const { periodId, basis, setPeriod, setBasis } = useAccountingContext();
	const routeSearch = Route.useSearch();

	useGlobalAccountingShortcuts();
	useAutoSelectPeriod();

	// Sync URL params → accounting context (on mount / URL change)
	useEffect(() => {
		if (routeSearch.period && routeSearch.period !== periodId) {
			setPeriod(routeSearch.period);
		}
		if (routeSearch.basis && routeSearch.basis !== basis) {
			setBasis(routeSearch.basis);
		}
	}, [routeSearch.period, routeSearch.basis]);

	const {
		isOpen: shortcutPanelOpen,
		setIsOpen: setShortcutPanelOpen,
		shortcuts: shortcutDefs,
	} = useShortcutPanel();

	// Default status bar items when page does not push its own
	const effectiveStatusBar = useMemo<StatusBarItem[]>(() => {
		const base: StatusBarItem[] = [];
		if (periodId) {
			base.push({
				id: "period",
				content: `${t("period")}: ${periodId}`,
				align: "left" as const,
			});
		}
		base.push({
			id: "basis",
			content: `${t("basis")}: ${basis.toUpperCase()}`,
			align: "left" as const,
		});
		if (statusBar.length > 0) {
			// Merge page-specific items (skip period/basis if page provides its own)
			const pageItems = statusBar.filter(
				(s) => s.id !== "period" && s.id !== "basis",
			);
			return [...base, ...pageItems];
		}
		return base;
	}, [statusBar, periodId, basis, t]);

	const config = useMemo<WorkspaceConfig>(
		() => ({
			menuBar,
			statusBar: effectiveStatusBar,
		}),
		[menuBar, effectiveStatusBar],
	);

	return (
		<Workspace config={config}>
			<ModuleErrorBoundary>
				<Outlet />
			</ModuleErrorBoundary>
			<ShortcutPanel
				open={shortcutPanelOpen}
				onOpenChange={setShortcutPanelOpen}
				shortcuts={shortcutDefs}
			/>
		</Workspace>
	);
}

// ── Main layout ─────────────────────────────────────────────────────

function AccountingLayout() {
	return <AccountingWorkspaceInner />;
}
